import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import type { GridSelection } from '@glideapps/glide-data-grid';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';

import {
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_TRANSACTIONS_SAVE_SUCCESS,
} from '@/constants/routes';

import { Nbsp } from '@/components/nbsp';
import { Button, Badge, cn } from '@simplywallst/ui-core/next';

import { useBeforeUnload, useUpdateHoldingTransactions } from '../hooks';

import type { Transaction } from '../hooks';

import {
  MenuCellRenderer,
  DateCellRenderer,
  SharePriceCellRenderer,
  SharesCellRenderer,
  TotalCostCellRenderer,
} from '../components/CustomCell';
import { createTickerCellRenderer } from './Ticker/Ticker';

import { Table, useTableValidation, useTableState } from '../components/Table';
import { MutationError, ValidationError } from '../components/Error';
import { Header } from './Header';

import type { Validation, Lang } from '../components/Table';

import {
  getErrorDetails,
  getTableDataFromTransactions,
  getTransactionsFromTableData,
} from '../utils';
import { getTickerData } from './utils';

const RowDeleteMobile = loadable(
  () => import('../components/RowDeleteMobile'),
  {
    resolveComponent: (module) => module.RowDeleteMobile,
  }
);

interface Props {
  portfolioId: string;
  holdingId: string;
  transactions: Transaction[];
}

export const PopulatedState = ({
  portfolioId,
  holdingId,
  transactions,
}: Props) => {
  const dispatch = useDispatch();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'megaTable',
  });

  const { isMobile, isTablet } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({
    defaultMatches: isMobile || isTablet,
  });

  useBeforeUnload();

  const { validateRows, highlightRegions } = useTableValidation();

  const [hideFooter, setHideFooter] = useState(false);
  const [gridSelection, setGridSelection] = useState<GridSelection>();
  const [selectedLanguage, setSelectedLanguage] = useState<Lang>('en-GB');
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>();
  const [showValidationError, setShowValidationError] = useState(false);
  const [validation, setValidation] = useState<Validation | null>(null);
  const [showMutationError, setShowMutationError] = useState(false);

  const {
    mutate,
    status: mutationStatus,
    error: mutationError,
  } = useUpdateHoldingTransactions({
    onSuccess: (data) => {
      const { id } = data.updateHoldingTransactions;
      dispatch({
        type: ROUTE_PORTFOLIO_TRANSACTIONS_SAVE_SUCCESS,
        payload: { portfolioId: id },
      });
    },
  });

  // Watch and reset error state when validation is successful
  useEffect(() => {
    if (showValidationError && validation?.error === null)
      setShowValidationError(false);
  }, [showValidationError, validation]);

  // Watch and reset mutation error state
  useEffect(() => {
    if (mutationStatus === 'error') return setShowMutationError(true);
    return setShowMutationError(false);
  }, [mutationStatus]);

  const tableData = useMemo(
    () => getTableDataFromTransactions(transactions || []),
    [transactions]
  );
  const tickerData = useMemo(() => getTickerData(tableData[0]), [tableData]);

  const tableState = useTableState(tableData, selectedLanguage);

  const { deleteRows, getRows } = tableState;

  const handleDiscard = useCallback(() => {
    trackEvent({
      modifier: 'discard',
      action: 'click',
      transactionCount: validation?.populatedRows.length,
      portfolioId,
    });
    dispatch({
      type: ROUTE_PORTFOLIO,
      payload: { portfolioId },
    });
  }, [dispatch, portfolioId, trackEvent, validation?.populatedRows.length]);

  const handleSave = useCallback(() => {
    const validation = validateRows(getRows());

    if (validation === null) return;

    trackEvent({
      modifier: 'save',
      action: 'click',
      transactionCount: validation.populatedRows.length,
      validationErrorCount: validation.invalidRows.length,
      portfolioId,
    });

    const { error } = validation;

    if (error !== null) {
      setValidation(validation);
      setShowValidationError(true);
      return;
    }

    const rows = validation.populatedRows.map(([, row]) => row);

    const transactions = getTransactionsFromTableData(rows);

    mutate(
      { id: holdingId, transactions },
      {
        onError: (error) => {
          const errorDetails = getErrorDetails(error);
          trackEvent({
            modifier: 'save',
            action: 'error',
            errorType: errorDetails.type,
            tickers: [tickerData.uniqueSymbol],
            transactionCount: transactions.length,
            portfolioId,
          });
        },
      }
    );
  }, [
    validateRows,
    getRows,
    trackEvent,
    portfolioId,
    mutate,
    holdingId,
    tickerData.uniqueSymbol,
  ]);

  const handleUpdate = useMemo(
    () => debounce(() => setValidation(validateRows(getRows())), 300),
    [validateRows, getRows]
  );

  const populatedRowCount = validation?.populatedRows.length ?? 0;
  const incompleteRowCount = validation?.invalidRows.length ?? 0;
  const ariaStatus = `${
    populatedRowCount - incompleteRowCount
  }/${populatedRowCount} transactions ready`;

  const showMobileRowDelete =
    isMobileScreen && gridSelection?.rows && gridSelection.rows.length > 0;

  return (
    <Track>
      <div className="grid h-dvh w-screen grid-rows-[max-content_auto] pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
        <Header company={transactions[0]} />
        <div
          className={cn(
            'relative grid',
            hideFooter ? 'grid-rows-[1fr]' : 'grid-rows-[1fr_max-content]'
          )}
        >
          <Table
            customRenderers={[
              MenuCellRenderer,
              DateCellRenderer,
              createTickerCellRenderer(tickerData),
              SharePriceCellRenderer,
              SharesCellRenderer,
              TotalCostCellRenderer,
            ]}
            scrollToIndex={scrollToIndex}
            onUpdate={handleUpdate}
            highlightRegions={
              showValidationError ? highlightRegions : undefined
            }
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            gridSelection={gridSelection}
            setGridSelection={setGridSelection}
            onCellActivated={({ id }) => {
              if (!isMobileScreen) return;
              if (
                id === 'transactionId' ||
                id === 'cost' ||
                id === 'ticker' ||
                id === 'type'
              ) {
                return;
              }
              setHideFooter(true);
            }}
            onFinishedEditing={() => setHideFooter(false)}
            {...tableState}
          />
          {hideFooter === false && (
            <div
              className={cn(
                'grid',
                showValidationError
                  ? 'grid-rows-[80px_72px] sm:grid-rows-[56px_72px]'
                  : 'grid-rows-[72px]'
              )}
            >
              {showValidationError && validation ? (
                <ValidationError
                  validation={validation}
                  onDismiss={() => setShowValidationError(false)}
                  onRangeClick={(rowIndex) => setScrollToIndex(rowIndex)}
                />
              ) : showMutationError ? (
                <MutationError
                  error={mutationError}
                  onDismiss={() => setShowMutationError(false)}
                />
              ) : null}

              {showMobileRowDelete ? (
                <RowDeleteMobile
                  selectedRows={gridSelection.rows.toArray()}
                  onUnselect={() => setGridSelection(undefined)}
                  onDelete={(rows: number[]) => {
                    deleteRows(rows);
                    setGridSelection(undefined);
                    handleUpdate();
                  }}
                />
              ) : (
                <div className="grid grid-cols-[minmax(auto,_1200px)] items-center justify-center bg-surface-1">
                  <div className="grid grid-cols-[max-content_max-content] items-center justify-between px-x2">
                    <Button variant="outline" onClick={handleDiscard}>
                      Discard
                      <span className="hidden sm:inline-block">
                        {' '}
                        and go back
                      </span>
                    </Button>
                    <div className="grid grid-cols-[max-content_max-content] items-center justify-end gap-x2">
                      <Badge className="px-x1_75 text-xs text-solid">
                        <span role="status" aria-label={ariaStatus}>
                          <strong>
                            {`${
                              populatedRowCount - incompleteRowCount
                            } / ${populatedRowCount}`}
                          </strong>
                          <Nbsp />
                          <Nbsp />
                          <span className="text-soft">
                            <span className="hidden sm:inline-block">
                              transactions
                              <Nbsp />
                            </span>
                            ready
                          </span>
                        </span>
                      </Badge>
                      <Button
                        variant="primary"
                        className="min-w-[100px]"
                        isDisabled={showValidationError}
                        isLoading={mutationStatus === 'pending'}
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Track>
  );
};
